import type {
  ProductInput,
  TechnicalPropertyInput,
  SelectedAccessory,
} from "./product.types";
import type { ProductStore } from "./productStore";
import type { SetState, GetState } from "../../types";
import { getProjectUnit, getProject } from "stores/project";
import { getLanguage } from "stores/globalStore";
import { getUser } from "stores/user";
import { toast } from "react-toastify";
import { load, sync, type GetProductStateVariables } from "./helpers";
import { Unit } from "variables/enums";
import type { FetchError } from "utils";
import type { SelectPart, TopologyConfiguration } from "./utils";
import {
  getInputFromOutput,
  selectPart,
  selectAccessory,
  setTechnicalProperty,
  createProductNode,
  getTopologyConfigurationFromProduct,
} from "./utils";
import type { Product } from "interfaces/schemas/nodeSchema";
import type { ResultOf } from "gql.tada";
import type { resultingListSubscription } from "api/subscription/product";
import { syncTechnicalPropertiesActiveTab } from "../ui/projectUIStore";

type ResultingListSubscriptionResult = ResultOf<
  typeof resultingListSubscription
>;

type ListViewItem = NonNullable<
  Extract<
    NonNullable<ResultingListSubscriptionResult["productListView"]>,
    { __typename?: "ProductListView" | undefined }
  >
>["listViewData"]["productListViewItems"][number];

export type GetProductStateArgs = Omit<
  GetProductStateVariables,
  "languageCode" | "countryCode" | "requestUnitsSystem" | "resultUnitsSystem"
>;

type LoadNode = Omit<
  GetProductStateArgs,
  "groupPosition" | "dummyType" | "wizardType"
> & {
  seriesId: number;
};

type LoadWizardNodeArgs = Required<
  Pick<GetProductStateArgs, "wizardType" | "dummyType" | "groupPosition">
>;

export type LoadWizardNode = GetProductStateArgs &
  LoadWizardNodeArgs & {
    seriesId: number;
  };

export type LoadProduct = Omit<
  ProductInput,
  | "wizardType"
  | "dummyType"
  | "groupPosition"
  | "languageCode"
  | "countryCode"
  | "requestUnitsSystem"
  | "resultUnitsSystem"
>;

type LoadWizardArgs = Required<
  Pick<ProductInput, "wizardType" | "dummyType" | "groupPosition">
>;

export type LoadWizardProduct = LoadProduct & LoadWizardArgs;

export type ProductActions = {
  setUnit: (unit: Unit) => void;
  selectPart: (part: Omit<SelectPart, "input">) => Promise<void>;
  selectAccessories: (accessories: SelectedAccessory[]) => Promise<void>;
  setTechnicalProperties: (
    properties: TechnicalPropertyInput[],
  ) => Promise<void>;
  selectStrategy: (strategyId: string) => Promise<void>;
  loadProduct: (
    variables: LoadProduct,
    switchProduct?: boolean,
  ) => Promise<void>;
  loadWizardProduct: (variables: LoadWizardProduct) => Promise<void>;
  loadNode: (variables: LoadNode) => Promise<void>;
  loadWizardNode: (variables: LoadWizardNode) => Promise<void>;
  loadResultListItem: (listItem: ListViewItem) => void;
  sync: (skipLoadingState?: boolean) => Promise<void>;
  syncAfterProductNodeUpdate: () => void;
  restoreNode: () => void;
  toProductNode: (quantity?: number) => Product | null;
  toPartialTopologyNode: () => TopologyConfiguration | null;
  clear: () => void;
};

export const createProductActions = (
  set: SetState<ProductStore>,
  get: GetState<ProductStore>,
): ProductActions => ({
  loadProduct: (variables, switchProduct = false) =>
    runAction(
      async ({ language, country, unit }) => {
        if (!variables || !variables?.seriesId) {
          throw new Error("seriesId is missing in variables for loadProduct");
        }
        if (switchProduct) {
          set({ isSwitchingProduct: true });
        }
        set({
          input: variables,
        });
        const state = await sync({
          ...variables,
          languageCode: language,
          countryCode: country,
          requestUnitsSystem: unit,
          resultUnitsSystem: unit,
        });
        if (!state.output) {
          throw new Error("Output is not available after syncing");
        }
        syncTechnicalPropertiesActiveTab(state.output);
        const newInput = getInputFromOutput(state.output, variables.seriesId);
        return {
          ...state,
          input: newInput,
          hasChanged: false,
          isSwitchingProduct: false,
        };
      },
      set,
      get,
    ),
  loadWizardProduct: (variables) =>
    runAction(
      async ({ language, country, unit }) => {
        set({
          input: variables,
        });
        const state = await sync({
          ...variables,
          languageCode: language,
          countryCode: country,
          requestUnitsSystem: unit,
          resultUnitsSystem: unit,
        });
        const input = get().input;
        if (!state.output) {
          throw new Error("Output is not available after syncing");
        }
        syncTechnicalPropertiesActiveTab(state.output);
        if (!input || !input?.seriesId) {
          throw new Error("Input was not set correctly, seriesId is missing");
        }
        const newInput = {
          ...getInputFromOutput(state.output, input.seriesId),
          wizardType: variables.wizardType,
          dummyType: variables.dummyType,
          groupPosition: variables.groupPosition,
        };
        return {
          ...state,
          input: newInput,
          hasChanged: false,
        };
      },
      set,
      get,
    ),
  setUnit: (unit: Unit) => {
    set({
      unit,
    });
    const input = get().input;
    if (input) {
      get().actions.sync();
    }
  },
  selectPart: (part) =>
    runAction(
      async () => {
        // Apply new changes to the order code based on the selected part and option
        const input = get().input;
        if (!input) {
          throw new Error("Input must be set before selecting a part");
        }
        selectPart({ ...part, input });
        // After modifying the order code, sync to get the updated output
        await get().actions.sync(true);
        const output = get().output;
        if (!output) {
          throw new Error("Output is not available after syncing");
        }
        // Update the input based on the new output to reflect any changes in the product state
        const updatedInput = getInputFromOutput(output, input.seriesId);
        return {
          input: { ...input, ...updatedInput },
          hasChanged: true,
        };
      },
      set,
      get,
    ),
  selectAccessories: (accessories) =>
    runAction(
      async () => {
        // Apply new changes to the selected accessories
        const input = get().input;
        const output = get().output;
        if (!output) {
          throw new Error("Output must be available to select accessories");
        }
        if (!input) {
          throw new Error("Input must be set before selecting an accessory");
        }
        for (const accessory of accessories) {
          selectAccessory({ ...accessory, input, output });
        }
        // After modifying the selected accessories, sync to get the updated output
        await get().actions.sync(true);
        if (!get().output) {
          throw new Error("Output is not available after syncing");
        }
        // Update the input based on the new output to reflect any changes in the product state
        const updatedInput = getInputFromOutput(output, input.seriesId);
        return {
          input: { ...input, ...updatedInput },
          hasChanged: true,
        };
      },
      set,
      get,
    ),
  setTechnicalProperties: (properties) =>
    runAction(
      async () => {
        const input = get().input;
        if (!input) {
          throw new Error(
            "Input must be set before setting a technical property",
          );
        }
        for (const property of properties) {
          setTechnicalProperty(property, input);
        }
        await get().actions.sync(true);
        const output = get().output;
        if (!output) {
          throw new Error("Output is not available after syncing");
        }
        const updatedInput = getInputFromOutput(output, input.seriesId);
        return {
          input: { ...input, ...updatedInput },
          hasChanged: true,
        };
      },
      set,
      get,
    ),
  selectStrategy: (strategyId) =>
    runAction(
      async () => {
        const input = get().input;
        if (!input) {
          throw new Error("Input must be set before selecting a strategy");
        }
        const newInput: ProductInput = {
          ...input,
          state: {
            ...input.state,
            currentStrategyId: strategyId,
          },
        };
        set({ input: newInput });
        await get().actions.sync(true);
        const output = get().output;
        if (!output) {
          throw new Error("Output is not available after syncing");
        }
        const updatedInput = getInputFromOutput(output, input.seriesId);
        return {
          input: { ...input, ...updatedInput },
          hasChanged: true,
        };
      },
      set,
      get,
    ),
  loadNode: (variables) =>
    runAction(
      async ({ language, country, unit }) => {
        const { seriesId, ...restVariables } = variables;
        const state = await load({
          ...restVariables,
          languageCode: language,
          countryCode: country,
          requestUnitsSystem: unit,
          resultUnitsSystem: unit,
        });
        if (!state.output) {
          throw new Error("Output is not available after loading node");
        }
        syncTechnicalPropertiesActiveTab(state.output);
        const input = getInputFromOutput(state.output, seriesId);
        return { ...state, input };
      },
      set,
      get,
    ),
  loadWizardNode: (variables) =>
    runAction(
      async ({ language, country, unit }) => {
        const { seriesId, ...restVariables } = variables;
        const state = await load({
          ...restVariables,
          languageCode: language,
          countryCode: country,
          requestUnitsSystem: unit,
          resultUnitsSystem: unit,
        });
        if (!state.output) {
          throw new Error("Output is not available after loading node");
        }
        syncTechnicalPropertiesActiveTab(state.output);
        const input = getInputFromOutput(state.output, seriesId);
        return {
          ...state,
          input: {
            ...input,
            wizardType: variables.wizardType,
            dummyType: variables.dummyType,
            groupPosition: variables.groupPosition,
          },
        };
      },
      set,
      get,
    ),
  loadResultListItem: (listItem) => {
    const input = get().input;
    if (!input) {
      throw new Error("Input must be set before loading a result list item");
    }
    if (!input.state) {
      throw new Error(
        "Input state must be set before loading a result list item",
      );
    }

    set({
      input: {
        ...input,
        state: {
          ...input.state,
          orderCodeState: listItem.ordercodeState,
          selectedAccessories: listItem.selectedAccessories,
        },
      },
    });
    get().actions.sync();
  },
  sync: (skipLoadingState = false) =>
    runAction(
      ({ language, country, unit }) => {
        const input = get().input;
        if (!input) {
          throw new Error("Input must be set before syncing");
        }
        return sync({
          ...input,
          languageCode: language,
          countryCode: country,
          requestUnitsSystem: unit,
          resultUnitsSystem: unit,
        });
      },
      set,
      get,
      skipLoadingState,
    ),
  toProductNode: (quantiy = 1) => {
    try {
      const input = get().input;
      if (!input) {
        throw new Error("Input must be set to convert to product node");
      }
      const output = get().output;
      if (!output) {
        throw new Error("Output must be available to convert to product node");
      }
      const productNode = createProductNode(input, output, quantiy);
      return productNode;
    } catch (error) {
      console.error("An error occurred whilst performing an action:", error);
    }
    return null;
  },
  toPartialTopologyNode: () => {
    try {
      const input = get().input;
      const output = get().output;
      if (!input || !output) {
        return null;
      }

      const topologyNode = getTopologyConfigurationFromProduct(
        input.seriesId,
        output,
      );
      return topologyNode;
    } catch (error) {
      console.error("An error occurred whilst performing an action:", error);
    }
    return null;
  },
  syncAfterProductNodeUpdate: () => set({ hasChanged: false }),
  restoreNode: () => {
    const nodeId = get().nodeId;
    const input = get().input;

    if (!nodeId) {
      if (!input?.seriesId) {
        throw new Error("seriesId is required to restore product");
      }
      return get().actions.loadProduct({ seriesId: input.seriesId });
    }

    const wizardNodeArgs = input?.wizardType
      ? {
          wizardType: input.wizardType,
          dummyType: input.dummyType,
          groupPosition: input.groupPosition,
        }
      : {};

    return runAction(
      ({ language, country, unit }) =>
        load({
          nodeId,
          languageCode: language,
          countryCode: country,
          requestUnitsSystem: unit,
          resultUnitsSystem: unit,
          ...wizardNodeArgs,
        }),
      set,
      get,
    );
  },
  clear: () =>
    set({
      output: null,
      input: null,
      unit: null,
      hasChanged: false,
      nodeId: null,
    }),
});

export type RunActionReturn = Promise<
  Partial<
    Pick<ProductStore, "output" | "input" | "unit" | "hasChanged" | "nodeId">
  >
>;

const runAction = async (
  actionFn: ({
    language,
    country,
    unit,
  }: {
    language: string;
    country: string;
    unit: Unit;
  }) => RunActionReturn,
  set: SetState<ProductStore>,
  get: GetState<ProductStore>,
  skipLoadingState = false,
) => {
  if (!skipLoadingState) {
    set({ isLoading: true });
  }
  set({ error: null });
  try {
    const unit = get().unit;
    const fallbackUnit: Unit = Unit.de_m3ph;
    if (!unit) {
      const projectUnit = getProjectUnit();
      const project = getProject();
      if (projectUnit) {
        set({ unit: projectUnit });
      } else {
        if (project) {
          console.error(
            "Project is available but unit is not. Defaulting to m3ph",
          );
        }
        set({ unit: fallbackUnit });
      }
    }
    // Get the current project, tree, and language from the store
    const user = getUser();
    if (!user) {
      throw new Error("User is not authenticated");
    }
    const language = getLanguage();
    if (!language) {
      throw new Error("Language is not set");
    }

    // Call the action function with the current state
    const state = await actionFn({
      language,
      country: user.tenant,
      unit: unit ?? fallbackUnit,
    });
    set(state);
  } catch (error) {
    const err = error as FetchError | Error;
    console.error("An error occurred whilst performing an action:", error);
    if (err instanceof Error) {
      toast.error(err.message, { type: "error", theme: "colored" });
    } else {
      const fetchErr = err as FetchError;
      if (fetchErr.type !== "abort") {
        const message =
          fetchErr.type === "graphql"
            ? (fetchErr.error.response.errors?.[0]?.message ??
              fetchErr.error.message)
            : fetchErr.error.message;
        toast.error(message, { type: "error", theme: "colored" });
      }
    }
    set({ error: err });
  } finally {
    if (!skipLoadingState) {
      set({ isLoading: false });
    }
  }
};
